import { useState, useMemo } from "react";
import {
  Card,
  BlockStack,
  Text,
  EmptyState,
  InlineStack,
  Box,
  Button,
  Banner,
  InlineGrid,
  SortableList,
  SortableResourceItem,
} from "@medentic/polaris-react";
import {
  PlusIcon,
  EditIcon,
  PlusCircleIcon,
  DeleteIcon,
} from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Event, FAQItem } from "@features/events/types";
import AddEditFAQModal from "@features/events/pages/events/components/faq/AddEditFAQModal";
import ImportFAQModal from "@features/events/pages/events/components/faq/ImportFAQModal";
import { getLocalizedText } from "@shared/lib";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import emptyFaqImage from "@shared/assets/empty-states/empty-faq.svg";

interface FAQItemComponentProps {
  faqItem: FAQItem;
  index: number;
  onEdit: (item: FAQItem, index: number) => void;
  onRemove: (index: number) => void;
}

const FAQItemComponent: React.FC<FAQItemComponentProps> = ({
  faqItem,
  index,
  onEdit,
  onRemove,
}) => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const answerClampStyle: React.CSSProperties = {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    maxWidth: "100%",
  };

  return (
    <SortableResourceItem
      accessibilityLabel={t("eventDetails.faq.editItem")}
      dragHandleAccessibilityLabel={`Reorder ${getLocalizedText(faqItem.question) || "FAQ item"}`}
    >
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <InlineStack blockAlign="center" gap="200" align="start">
          <div style={{ flex: 1, minWidth: 0 }}>
            <BlockStack>
              <Text as="span" variant="bodyMd" fontWeight="medium" truncate>
                {getLocalizedText(faqItem.question)}
              </Text>
              <div style={answerClampStyle}>
                <Text as="span" variant="bodySm" tone="subdued">
                  {getLocalizedText(faqItem.answer)}
                </Text>
              </div>
            </BlockStack>
          </div>

          <Box opacity={isHovered ? "1" : "0"}>
            <InlineStack gap="200" blockAlign="center">
              <Button
                icon={EditIcon}
                variant="plain"
                size="slim"
                onClick={() => onEdit(faqItem, index)}
                accessibilityLabel={t("eventDetails.faq.editItem")}
              />
              <Button
                icon={DeleteIcon}
                variant="plain"
                size="slim"
                tone="critical"
                onClick={() => onRemove(index)}
                accessibilityLabel={t("eventDetails.faq.removeItem")}
              />
            </InlineStack>
          </Box>
        </InlineStack>
      </div>
    </SortableResourceItem>
  );
};

interface FAQCardProps {
  eventData: Partial<Event>;
  onDataChange: (updates: Partial<Event>) => void;
  fieldErrors?: Record<string, string>;
}

export default function FAQCard({
  eventData,
  onDataChange,
  fieldErrors = {},
}: FAQCardProps) {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<FAQItem | null>(null);
  const [editingIndex, setEditingIndex] = useState<number>(-1);

  const faqItems = eventData.faq || [];

  // Fetch all events to enable FAQ import
  const { GET } = useApiRequest();
  const { eventOrganizerId: hcc } = useParams<{ eventOrganizerId: string }>();
  const { data: allEvents = [], isLoading: allEventsLoading } = useQuery<Event[]>({
    queryKey: ["events", hcc],
    queryFn: () => GET(`/api/healthcare-companies/${hcc}/e/events?`),
    enabled: Boolean(hcc),
    placeholderData: keepPreviousData,
  });

  // Filter events that have FAQs (exclude current event)
  const eventsWithFAQ = useMemo(() => {
    return allEvents.filter(
      (event) => event.id !== eventData.id && event.faq && event.faq.length > 0
    );
  }, [allEvents, eventData.id]);

  // Get FAQ related errors
  const faqErrors = useMemo(() => {
    const errors: string[] = [];
    Object.keys(fieldErrors).forEach((key) => {
      if (key.startsWith("faq.")) {
        errors.push(fieldErrors[key]);
      }
    });
    return errors;
  }, [fieldErrors]);

  const renderFAQItem = (
    faqItem: FAQItem
  ) => {
    const itemIndex = faqItems.indexOf(faqItem);
    return (
      <FAQItemComponent
        faqItem={faqItem}
        index={itemIndex}
        onEdit={handleEditFAQ}
        onRemove={handleRemoveFAQ}
      />
    );
  };

  const handleAddFAQ = () => {
    setEditingItem(null);
    setEditingIndex(-1);
    setIsModalOpen(true);
  };

  const handleEditFAQ = (item: FAQItem, index: number) => {
    setEditingItem(item);
    setEditingIndex(index);
    setIsModalOpen(true);
  };

  const handleRemoveFAQ = (index: number) => {
    const updatedFAQ = faqItems.filter((_, i) => i !== index);
    onDataChange({ faq: updatedFAQ.length > 0 ? updatedFAQ : undefined });
  };

  const handleSaveFAQ = (faqItem: FAQItem) => {
    if (editingIndex >= 0) {
      // Edit existing item
      const updatedFAQ = [...faqItems];
      updatedFAQ[editingIndex] = faqItem;
      onDataChange({ faq: updatedFAQ });
    } else {
      // Add new item
      const updatedFAQ = [...faqItems, faqItem];
      onDataChange({ faq: updatedFAQ });
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    setEditingIndex(-1);
  };

  const handleImportFAQ = (importedFAQItems: FAQItem[]) => {
    const updatedFAQ = [...faqItems, ...importedFAQItems];
    onDataChange({ faq: updatedFAQ });
  };

  const hasFAQ = faqItems.length > 0;

  return (
    <>
      <Card>
        <BlockStack gap="400">
          <InlineGrid columns="1fr auto">
            <BlockStack gap="100">
              <Text as="h2" variant="headingMd">
                {t("eventDetails.faq.title")}
              </Text>
              <Text as="p" variant="bodyMd" tone="subdued">
                {t("eventDetails.faq.description")}
              </Text>
            </BlockStack>
            <Box>
              <Button
                variant="plain"
                icon={PlusCircleIcon}
                onClick={() => setIsImportModalOpen(true)}
                accessibilityLabel={t("eventDetails.faq.import_cta")}
              >
                {t("eventDetails.faq.import_cta")}
              </Button>
            </Box>
          </InlineGrid>

          {faqErrors.length > 0 && (
            <Banner tone="critical">
              <BlockStack gap="100">
                {faqErrors.map((error, index) => (
                  <Text key={index} as="p" variant="bodyMd">
                    {error}
                  </Text>
                ))}
              </BlockStack>
            </Banner>
          )}

          {hasFAQ ? (
            <BlockStack gap="200">
              <Box
                borderColor="border"
                borderWidth="025"
                borderRadius="200"
                background="bg-surface"
              >
                <SortableList
                  items={faqItems}
                  onChange={(reorderedFAQ) => {
                    onDataChange({ faq: reorderedFAQ });
                  }}
                  idForItem={(faqItem) => {
                    // Use a simple hash of the item content for stable IDs
                    const content = `${Object.values(faqItem?.question).join(
                      "-"
                    )}-${Object.values(faqItem?.answer).join("-")}`;
                    return `faq-${content
                      .replace(/[^a-zA-Z0-9]/g, "")
                      .substring(0, 20)}`;
                  }}
                  renderItem={(item) => renderFAQItem(item)}
                  showDividers={true}
                />
              </Box>
              <InlineStack>
                <Button
                  variant="plain"
                  icon={PlusCircleIcon}
                  onClick={handleAddFAQ}
                >
                  {t("eventDetails.faq.add_cta")}
                </Button>
              </InlineStack>
            </BlockStack>
          ) : (
            <EmptyState
              heading={t("eventDetails.faq.emptyState")}
              action={{
                content: t("eventDetails.faq.add_cta"),
                icon: PlusIcon,
                onAction: handleAddFAQ,
              }}
              image={emptyFaqImage}
            />
          )}
        </BlockStack>
      </Card>

      <AddEditFAQModal
        key={`${isModalOpen}-${editingItem ? "edit" : "new"}`}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveFAQ}
        editingItem={editingItem}
      />

      <ImportFAQModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImport={handleImportFAQ}
        eventsWithFAQ={eventsWithFAQ}
        loading={allEventsLoading}
      />
    </>
  );
}
